import * as React from 'react'
import update from 'immutability-helper'
import {
  FlowFormElement,
  FORM_ELEMENT_TYPES,
  Select,
  Input,
  ConfirmAction,
  NewButton,
  SvgIcon,
  getFlowElement,
  ElementsFlow,
  NotificationType,
  Item,
  StepConditionType,
  StepConditionTypeNames,
} from 'prace-common-components'
import useElementSelects from 'util/useElementSelects'
import { FlowElementIds, FormElement } from 'components/FormModal/types'
import Grid from '@material-ui/core/Grid'
import { $ElementsContainer, $ElementItem, $ElementRow, $ActionsContainer, $SectionLabel, $HelperText, $FieldsGrid, $Summary, $TitleSpan, $ElementSpan } from './styles'
import { AllocationDecisionOptionsProps } from './types'
import { RootState } from 'store'
import { useAppDispatch, useAppSelector } from 'store/hooks'

const emptyFlowElement = (stepId: number): FlowElementIds => ({
  step: stepId, form: null, element: null, groupElement: null,
})

export const AllocationDecisionOptions: React.FC<AllocationDecisionOptionsProps> = (
  { step, config, onChange, outcomeLabel = 'awarded', optional },
) => {
  const dispatch = useAppDispatch()
  const call = useAppSelector((state: RootState) => state.calls.call)

  const { elements, condition, value } = config
  const elementsLength = elements.length

  const [editFlowElement, setEditFlowElement] = React.useState<FlowElementIds>(emptyFlowElement(step.id))
  const [editElementIdx, setEditElementIdx] = React.useState<number>(elementsLength)

  const notEditingElement = editElementIdx === elementsLength

  const [updatedEditFlowElement, elementSelects] = useElementSelects(
    [step],
    editFlowElement,
    'Select elements from this step',
    false,
    false,
    false,
    false,
  )

  const nonValueTypes = condition === StepConditionType.empty || condition === StepConditionType.nonEmpty

  let foundSelect: Nullable<FlowFormElement> = null
  let foundCheckBox: Nullable<FlowFormElement> = null
  elements.forEach((el: { id: number }) => {
    const flowEl = getFlowElement(call, el.id).element
    const type = flowEl?.elementType
    if (
      type == FORM_ELEMENT_TYPES.SELECT ||
      type == FORM_ELEMENT_TYPES.MULTISELECT ||
      type == FORM_ELEMENT_TYPES.TOGGLE ||
      type == FORM_ELEMENT_TYPES.SELECT_PARTITION
    ) foundSelect = flowEl
    if (type == FORM_ELEMENT_TYPES.CHECKBOX) foundCheckBox = flowEl
  })

  const validationTypeItems = React.useMemo(() =>
    Object.values(StepConditionType)
      .map((t) => ({ value: t, label: StepConditionTypeNames[t] })),
  [])

  const valueConfig = React.useMemo(() => {
    if (!condition || nonValueTypes) return null
    const isTextValue = condition === StepConditionType.equal || condition === StepConditionType.different
    const elementType = (foundSelect || foundCheckBox)
      ? FORM_ELEMENT_TYPES.SELECT
      : isTextValue ? FORM_ELEMENT_TYPES.TEXT : FORM_ELEMENT_TYPES.NUMBER
    const items: Item[] = foundCheckBox
      ? [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }]
      : foundSelect ? (foundSelect!.optionsGroup?.options || []) : []
    return { isRange: false, elementType, items }
  }, [condition, foundSelect, foundCheckBox, nonValueTypes])

  const handleChange = (name: string, value: Nullable<StringNumber>) => {
    if (['step', 'form', 'element', 'groupElement'].includes(name)) {
      const newEditFlowElement = update(updatedEditFlowElement as FlowElementIds, { [name]: { $set: value } })
      if (name === 'element' && notEditingElement) addElement(newEditFlowElement)
      else setEditFlowElement(newEditFlowElement)
    } else if (name === 'validationType') {
      onChange({ ...config, condition: value as StepConditionType, value: '' })
    } else if (name === 'value') {
      onChange({ ...config, value: String(value || '') })
    }
  }

  const validElement = (ef?: FlowElementIds): boolean => {
    const editingEl = ef ? ef : updatedEditFlowElement as FlowElementIds
    if (!editingEl.element) return false
    const { element } = editingEl
    const isDuplicate = elements.some((addedEl, i) => addedEl.id == element && editElementIdx !== i)
    if (isDuplicate) {
      dispatch({ type: 'notification', payload: { type: NotificationType.error, msg: 'Cannot add duplicates' } })
    }
    return !isDuplicate
  }

  const addElement = (ef?: FlowElementIds) => {
    if (!validElement(ef)) return
    const { element } = (ef ? ef : updatedEditFlowElement) as FlowElementIds
    onChange({ ...config, elements: [...elements, { id: element as number }] })
    setEditFlowElement(emptyFlowElement(step.id))
    setEditElementIdx((prev) => prev + 1)
  }

  const editElement = (idx: number) => {
    setEditElementIdx(idx)
    const { form, element } = getFlowElement(call, elements[idx].id)
    setEditFlowElement({
      step: step.id,
      form: form ? form.id : null,
      element: element ? element.id : null,
      groupElement: null,
    })
  }

  const handleEdit = () => {
    if (!validElement()) return
    const { element } = updatedEditFlowElement as FlowElementIds
    const newElements = [...elements]
    newElements[editElementIdx] = { id: element as number }
    onChange({ ...config, elements: newElements })
    setEditFlowElement(emptyFlowElement(step.id))
    setEditElementIdx(elements.length)
  }

  const handleDeleteElement = () => {
    const newElements = elements.filter((_, i) => i !== editElementIdx)
    onChange({ ...config, elements: newElements })
    setEditFlowElement(emptyFlowElement(step.id))
    setEditElementIdx(newElements.length)
  }

  const addedElements: ElementsFlow[] = elements.map(
    (el: { id: number }) => getFlowElement(call, el.id),
  )

  // Skip the step select (index 0) — step is fixed to the current step
  const formElementSelects = (elementSelects as FormElement[]).slice(1)

  const summary = () => {
    if (!condition || !elements.length) return null
    const elementNames = addedElements
      .filter((el) => el.form)
      .map((el) => el.element?.title || '')
    if (!elementNames.length) return null

    const singleElement = elementNames.length === 1
    const subTitle = singleElement ? elementNames[0] : `the ${elementNames.length} elements`

    let typeText = ''
    let needsValue = true
    switch (condition) {
      case StepConditionType.equal:
        typeText = ` ${singleElement ? 'is' : 'are'} equal to`
        break
      case StepConditionType.different:
        typeText = ` ${singleElement ? 'is' : 'are'} different from`
        break
      case StepConditionType.moreThan:
        typeText = ` ${singleElement ? 'is' : 'are'} more than`
        break
      case StepConditionType.lessThan:
        typeText = ` ${singleElement ? 'is' : 'are'} less than`
        break
      case StepConditionType.nonEmpty:
        typeText = ` ${singleElement ? 'is' : 'are'} not empty`
        needsValue = false
        break
      case StepConditionType.empty:
        typeText = ` ${singleElement ? 'is' : 'are'} empty`
        needsValue = false
        break
      case StepConditionType.includes:
        typeText = ` ${singleElement ? 'includes' : 'include'}`
        break
      case StepConditionType.excludes:
        typeText = ` ${singleElement ? 'excludes' : 'exclude'}`
        break
      default:
        break
    }

    if (needsValue && !value) return null

    const displayValue = valueConfig?.items?.find((item) => String(item.value) === String(value))?.label || value

    return (
      <$Summary>
        <$TitleSpan>IF</$TitleSpan>{' '}
        <$ElementSpan>{subTitle}</$ElementSpan>
        {typeText}
        {needsValue && value ? ` "${displayValue}"` : ''},
        {' '}<$TitleSpan>THEN</$TitleSpan> the application is <$ElementSpan>{outcomeLabel}</$ElementSpan>.
      </$Summary>
    )
  }

  return (
    <div>
      <$SectionLabel>Decision Rule</$SectionLabel>
      {optional && <$HelperText>Optional: this step type can be saved without a decision rule.</$HelperText>}

      {/* Added elements list */}
      {addedElements.length > 0 && (
        <$ElementsContainer>
          {addedElements.map((addedEl: ElementsFlow, i: number) =>
            addedEl.form ? (
              <$ElementRow key={i} container alignItems='center' onClick={() => editElement(i)}>
                <$ElementItem>
                  {`${i + 1}. ${addedEl.form?.title} > ${addedEl.element?.title}`}
                </$ElementItem>
                <SvgIcon name='altEdit' clickable size={16} />
              </$ElementRow>
            ) : null,
          )}
        </$ElementsContainer>
      )}

      {/* Element picker selects + action buttons */}
      <$FieldsGrid container spacing={2}>
        {formElementSelects.map((select: FormElement) => (
          <Grid item xs={12} key={select.id}>
            <Select
              name={select.name}
              value={select.value as string}
              items={select.items || []}
              {...select.options}
              onChange={handleChange}
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          {notEditingElement ? (
            <NewButton onClick={() => addElement()} creator variant='outlined'>Add Element</NewButton>
          ) : (
            <$ActionsContainer container>
              <ConfirmAction
                deletion
                title='Remove Element'
                description='This action will remove the element from the decision rule.'
                onConfirm={handleDeleteElement}
              >
                <NewButton error variant='outlined'>Remove element</NewButton>
              </ConfirmAction>
              <NewButton onClick={handleEdit} variant='outlined'>Edit element</NewButton>
            </$ActionsContainer>
          )}
        </Grid>

        {/* Validation type and value side by side */}
        <Grid item xs={valueConfig && !valueConfig.isRange ? 6 : 12}>
          <Select
            name='validationType'
            title='Validation Type'
            value={condition as string}
            items={validationTypeItems}
            placeholder='Choose a validation type'
            disabled={!notEditingElement}
            onChange={handleChange}
          />
        </Grid>

        {valueConfig && !valueConfig.isRange && (
          <Grid item xs={6}>
            {valueConfig.elementType === FORM_ELEMENT_TYPES.SELECT ? (
              <Select
                name='value'
                title='Value'
                value={value || null}
                items={valueConfig.items}
                placeholder='Choose a value'
                disabled={!notEditingElement}
                onChange={handleChange}
              />
            ) : (
              <Input
                name='value'
                title='Value'
                value={value || ''}
                type={valueConfig.elementType as FORM_ELEMENT_TYPES}
                hideItalic
                disabled={!notEditingElement}
                onChange={handleChange}
              />
            )}
          </Grid>
        )}
      </$FieldsGrid>
      {summary()}
    </div>
  )
}
